import { until } from '@vueuse/core'

import { useUser } from '~/composables/useUser'
import { hasRole, Role } from '~~/shared/Roles'

// Pages only, and every entry needs its counterpart in server/middleware/01_auth.ts —
// that one runs first and redirects to /login before this middleware is ever reached,
// so an entry here without one there does nothing.
//
// '/__nuxt_error' is the route Nuxt renders app/error.vue through, so it has to be
// public in both places or an error shown to anyone not signed in turns into a
// redirect to /login. It also has to skip the isPending wait below: that wait has a
// 5s ceiling, and an error page is the one render that must not sit on it.
//
// A '/error' entry stood here until this was traced. Nothing was ever served at that
// path — it is not the route above and there is no pages/error.vue — so it only ever
// widened the exemption for anything later named '/error*'.
const PUBLIC_PATHS = ['/login', '/kiosk', '/__nuxt_error']

const isPublic = (path: string) => {
  return PUBLIC_PATHS.some(p => path === p || path.startsWith(`${p}/`))
}

const CANTEEN_ADMIN_PREFIXES = ['/admin/orders', '/admin/statistics', '/admin/products', '/admin/print-orders']

const getRequiredRole = (path: string): Role | undefined => {
  if (CANTEEN_ADMIN_PREFIXES.some(prefix => path.startsWith(prefix))) return Role.CanteenAdmin
  if (path.startsWith('/admin/password')) return Role.PasswordAdmin
  return
}

export default defineNuxtRouteMiddleware(async (to) => {
  if (isPublic(to.path)) return

  const { isAuthenticated, isPending, roles } = useUser()

  if (isPending.value) {
    await Promise.race([
      until(isPending).toBe(false),
      new Promise(resolve => setTimeout(resolve, 5000)),
    ])
  }

  if (!isAuthenticated.value) {
    return navigateTo({
      path: '/login',
      query: { redirect: to.fullPath },
    })
  }

  const requiredRole = getRequiredRole(to.path)
  if (requiredRole && !hasRole(roles.value, requiredRole)) {
    throw createError({ statusCode: 403, statusMessage: 'Du har ikke rettigheder til denne side' })
  }
})
